import {
  Bot,
  Building2,
  Calendar,
  Code,
  History,
  Import,
  Mail,
  Phone,
  Plug,
  Settings,
  Sparkles,
  Sun,
  TrendingUp,
  User,
  Users,
  UsersRound,
} from "lucide-react";
import { CanAccess, useGetList, useTranslate } from "ra-core";
import { Link, NavLink, useLocation } from "react-router";
import * as React from "react";

import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { EmailAccountsPage } from "../settings/EmailAccountsPage";
import { useUserMenu } from "ra-core";

const RAIL_ACTIVE =
  "bg-[color-mix(in_oklch,var(--accent-solid)_14%,transparent)] text-[var(--accent-solid)]";
const RAIL_IDLE =
  "text-muted-foreground hover:text-foreground hover:bg-muted/60";

function RailLink({
  icon,
  label,
  to,
  end = false,
  activePaths,
  count,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  end?: boolean;
  activePaths?: string[];
  count?: number | null;
}) {
  const location = useLocation();
  const extraActive =
    activePaths?.some((p) =>
      end ? location.pathname === p : location.pathname.startsWith(p),
    ) ?? false;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={to}
          end={end}
          aria-label={label}
          className={({ isActive }) =>
            cn(
              "relative grid place-items-center size-10 rounded-xl transition-colors no-underline",
              isActive || extraActive ? RAIL_ACTIVE : RAIL_IDLE,
            )
          }
        >
          <span className="[&>svg]:size-[18px]">{icon}</span>
          {typeof count === "number" && count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 grid place-items-center rounded-full bg-[var(--accent-solid)] text-[9px] font-semibold text-white tabular-nums">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function AdminLink({
  icon,
  label,
  to,
  end = false,
  activePaths,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  end?: boolean;
  activePaths?: string[];
}) {
  const location = useLocation();
  const extraActive =
    activePaths?.some((p) =>
      end ? location.pathname === p : location.pathname.startsWith(p),
    ) ?? false;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 px-2.5 h-9 rounded-md text-[13px] no-underline transition-colors",
          isActive || extraActive
            ? "bg-[color-mix(in_oklch,var(--accent-solid)_12%,transparent)] text-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
        )
      }
    >
      <span className="[&>svg]:size-4">{icon}</span>
      {label}
    </NavLink>
  );
}

const ADMIN_PATHS = [
  "/team",
  "/sales",
  "/settings",
  "/profile",
  "/admin/agent",
  "/agent",
  "/strategy",
];

function isInAdminSection(pathname: string): boolean {
  return ADMIN_PATHS.some((p) => pathname.startsWith(p));
}

function AdminRailMenu() {
  const location = useLocation();
  const active = isInAdminSection(location.pathname);
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Admin"
              className={cn(
                "grid place-items-center size-10 rounded-xl transition-colors",
                active ? RAIL_ACTIVE : RAIL_IDLE,
              )}
            >
              <Settings className="size-[18px]" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Admin</TooltipContent>
      </Tooltip>
      <PopoverContent
        side="right"
        align="end"
        className="w-56 p-1.5 space-y-0.5"
      >
        <AdminLink
          icon={<TrendingUp />}
          label="Stratégie Revenu"
          to="/strategy"
        />
        <AdminLink
          icon={<UsersRound />}
          label="Équipe"
          to="/team"
          activePaths={["/team", "/sales"]}
        />
        <AdminLink
          icon={<Plug />}
          label="Intégrations"
          to="/settings/integrations"
        />
        <AdminLink
          icon={<Mail />}
          label="Comptes email"
          to="/settings/email-accounts"
        />
        <AdminLink
          icon={<Sparkles />}
          label="Providers IA"
          to="/settings/ai-providers"
        />
        <AdminLink
          icon={<Settings />}
          label="Configuration"
          to="/settings"
          end
        />
        <AdminLink icon={<User />} label="Profil" to="/profile" />
        <AdminLink
          icon={<Bot />}
          label="Agent"
          to="/admin/agent"
          activePaths={["/admin/agent", "/agent"]}
        />
      </PopoverContent>
    </Popover>
  );
}

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User className="size-4" />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const TeamMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/team" className="flex items-center gap-2">
        <UsersRound className="size-4" />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings className="size-4" />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const EmailAccountsMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={EmailAccountsPage.path} className="flex items-center gap-2">
        <Mail className="size-4" />
        Comptes email
      </Link>
    </DropdownMenuItem>
  );
};

const ImportMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import className="size-4" />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

function useSidebarCount(resource: string, filter?: Record<string, unknown>) {
  const { total } = useGetList(resource, {
    pagination: { page: 1, perPage: 1 },
    filter,
  });
  return typeof total === "number" ? total : null;
}

export function FabrikSidebar() {
  const { title } = useConfigurationContext();

  const contactsCount = useSidebarCount("contacts");
  const companiesCount = useSidebarCount("companies");
  const devTasksCount = useSidebarCount("dev_tasks", {
    "archived_at@is": null,
  });

  return (
    <aside className="hidden md:flex w-16 shrink-0 flex-col items-center bg-sidebar border-r border-border h-full overflow-hidden">
      {/* Logo */}
      <div
        className="h-14 flex items-center justify-center shrink-0"
        title={title}
      >
        <div className="size-8 rounded-xl bg-[var(--accent-solid)] grid place-items-center">
          <span className="text-sm font-bold text-white">F</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 flex flex-col items-center gap-1 py-2 overflow-y-auto">
        <RailLink icon={<Sun />} label="Aujourd'hui" to="/today" />
        <RailLink icon={<Phone />} label="Appels" to="/appels" />
        <RailLink
          icon={<Users />}
          label="Contacts"
          to="/contacts"
          count={contactsCount}
        />
        <RailLink
          icon={<Building2 />}
          label="Entreprises"
          to="/companies"
          count={companiesCount}
        />
        <RailLink
          icon={<Code />}
          label="Dev"
          to="/dev"
          activePaths={["/dev", "/dev_tasks"]}
          count={devTasksCount}
        />
        <RailLink icon={<History />} label="Changelog" to="/changelog" />
        <RailLink
          icon={<Calendar />}
          label="Calendrier"
          to="/calendar"
          activePaths={["/calendar", "/appointments"]}
        />
        <CanAccess resource="configuration" action="edit">
          <AdminRailMenu />
        </CanAccess>
      </nav>

      {/* Footer */}
      <div className="py-3 flex flex-col items-center gap-2 shrink-0">
        <ThemeModeToggle />
        <UserMenu>
          <ProfileMenu />
          <CanAccess resource="sales" action="list">
            <TeamMenu />
          </CanAccess>
          <CanAccess resource="configuration" action="edit">
            <SettingsMenu />
          </CanAccess>
          <CanAccess resource="email_accounts" action="list">
            <EmailAccountsMenu />
          </CanAccess>
          <ImportMenuItem />
        </UserMenu>
      </div>
    </aside>
  );
}
